import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { employeesApi } from '../../services/employees';
import { employeeTimeAllocationApi } from '../../services/employeeTimeAllocation';
import { useAuth } from '../../context/AuthContext';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/Pagination';

function EmployeeList() {
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [filterType, setFilterType] = useState('all');
  const { pagination, handlePageChange, updatePagination } = usePagination();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleClear = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('');
    setFilterType('all');
  }, []);

  const getFridayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -2 : 5);
    const friday = new Date(d.setDate(diff));
    return friday.toISOString().split('T')[0];
  };

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      let employeesData = [];

      if (filterType === 'missing_assets') {
        const data = await employeesApi.getWithoutAssets();
        employeesData = Array.isArray(data) ? data : (data.data || []);
      } else if (filterType === 'missing_occupancy_last_week') {
        const lastWeek = new Date();
        lastWeek.setDate(lastWeek.getDate() - 7);
        const lastWeekFriday = getFridayOfWeek(lastWeek);
        const data = await employeeTimeAllocationApi.getMissing(lastWeekFriday);
        employeesData = Array.isArray(data) ? data : (data.data || []);
      } else if (filterType === 'missing_occupancy_this_week') {
        const today = new Date();
        const thisWeekFriday = getFridayOfWeek(today);
        const data = await employeeTimeAllocationApi.getMissing(thisWeekFriday);
        employeesData = Array.isArray(data) ? data : (data.data || []);
      } else {
        const response = await employeesApi.getAll({
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          status: statusFilter
        });
        employeesData = response.data || response;
        updatePagination(response.pagination);
      }

      setEmployees(employeesData);
    } catch (err) {
      setError('Failed to load employees');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterType, pagination.page, pagination.limit, updatePagination, searchQuery, statusFilter]);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filterType, statusFilter]);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    // For special filter types, search is done client-side via filteredEmployees
    // For 'all' filter, search is done server-side
    if (filterType === 'all') {
      fetchEmployees();
    }
    // For other filter types, the client-side filter will handle it
  }, [fetchEmployees, filterType]);

  // Redirect if user doesn't have admin role
  if (!hasRole('super_admin', 'admin_head', 'hr_head', 'accounts_head')) {
    return <Navigate to={`/employees/${user.id}`} />;
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) {
      return;
    }
    
    try {
      await employeesApi.delete(id);
      setEmployees(employees.filter(e => e.id !== id));
    } catch (err) {
      setError('Failed to delete employee');
      console.error(err);
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      active: 'badge badge-active',
      inactive: 'badge badge-inactive',
      exited: 'badge badge-rejected'
    };
    return <span className={classes[status] || 'badge'}>{status ? status.replace('_', ' ') : 'Unknown'}</span>;
  };

  const filteredEmployees = employees.filter(emp =>
    (searchQuery === '' ||
    emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.role?.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (statusFilter === '' || emp.status === statusFilter)
  );

  // Use filtered results for special filter types, server results for 'all' filter
  const displayEmployees = filterType === 'all' ? employees : filteredEmployees;

  // Track if search or filter is active
  const isFilterActive = searchQuery || statusFilter || filterType !== 'all';

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading employees...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>Employees</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          {filterType === 'all' ? (
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px', flex: 1 }}>
              <input
                type="text"
                placeholder="Search by name or email..."
                className="form-control form-control-compact-lg"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
          ) : (
            <input
              type="text"
              placeholder="Search by name or email..."
              className="form-control form-control-compact-lg"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1 }}
            />
          )}
          <select
            className="form-control form-control-compact-md"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Employees</option>
            <option value="missing_assets">Missing Assets</option>
            <option value="missing_occupancy_last_week">Missing Time Allocation (Last Week)</option>
            <option value="missing_occupancy_this_week">Missing Time Allocation (This Week)</option>
          </select>
          <select
            className="form-control form-control-compact-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="exited">Exited</option>
          </select>
          {isFilterActive && (
            <button className="btn btn-secondary btn-compact" onClick={handleClear}>
              Clear
            </button>
          )}
          {hasRole('hr_head', 'super_admin') && (
            <Link to="/employees/new" className="btn btn-primary btn-compact">
              Add Emp
            </Link>
          )}
        </div>
      </div>

      {error && (
        <div className="card" style={{ backgroundColor: '#ffebee', color: '#c62828', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Department</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined Date</th>
                <th>Manager</th>
                <th>Actions</th>
              </tr>
            </thead>
          <tbody>
            {displayEmployees.map(employee => (
              <tr 
                key={employee.id} 
                onClick={() => navigate(`/employees/${employee.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <td>{employee.name}</td>
                <td>{employee.email}</td>
                <td>{employee.department}</td>
                <td>{employee.role}</td>
                <td>{getStatusBadge(employee.status)}</td>
                <td>{employee.hire_date ? new Date(employee.hire_date).toLocaleDateString() : '-'}</td>
                <td>{employee.manager_name || '-'}</td>
                <td>
                  <div className="action-buttons">
                    <Link 
                      to={`/employees/${employee.id}`} 
                      className="btn-icon btn-view"
                      title="View"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </Link>
                    {hasRole('hr_head', 'super_admin') && (
                      <>
                        <Link
                          to={`/employees/${employee.id}/edit`}
                          className="btn-icon btn-edit"
                          title="Edit"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </Link>
                        {hasRole('admin_head', 'super_admin') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(employee.id);
                            }}
                            className="btn-icon btn-delete"
                            title="Delete"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
        {displayEmployees.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No employees found
          </div>
        )}
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
      </div>
    </div>
  );
}

export default EmployeeList;
